import { useFormContext, useWatch } from "react-hook-form";
import { EventCreateEditFormValues } from "../../EventCreateEditModal/useEventCreateEditForm";
import { useMemo } from "react";
import { getRecurrencePatternDates } from "shared/utils";
import _ from "lodash";
import { Combobox, useFilter } from "@ttc3k/trekker";

export const useSkipDates = () => {
  const { control, setValue } = useFormContext<EventCreateEditFormValues>();

  // Watch for changes in the recurrence pattern, startDate, and the skipDates
  const recurrencePattern = useWatch({ control, name: "recurrencePattern" });
  const startDate = useWatch({ control, name: "startDate" });
  const skipDates = useWatch({ control, name: "recurrencePattern.skipDates" });

  /**
   * Memoize the skippable dates
   * @returns An array of skippable dates
   */
  const skippableDates = useMemo<{ label: string; value: string }[]>(() => {
    const dates = getRecurrencePatternDates(recurrencePattern, startDate).map(
      date => ({
        label: date,
        value: date,
      }),
    );

    // We need to ensure that any current skip dates are still valid (i.e., they match the pattern)
    const validSkipDates =
      skipDates?.filter(date => dates.some(d => d.value === date)) ?? [];
    if (skipDates?.length !== validSkipDates.length) {
      setValue("recurrencePattern.skipDates", validSkipDates, {
        shouldDirty: true,
      });
    }

    return dates;
  }, [recurrencePattern, skipDates, startDate, setValue]);

  const { contains } = useFilter({ sensitivity: "base" });

  const { collection, filter, set } = Combobox.useCollection({
    initialItems: skippableDates,
    filter: contains,
  });

  // When the user types in the combobox, filter the dates
  const handleComboboxInputValueChange = (search: string) => {
    filter(search);
  };

  const handleComboboxOpenChange = (open: boolean) => {
    if (open) {
      set(skippableDates);
    }
  };

  // When a date is selected, update the skipDates
  const handleComboboxValueChange = (value: string[]) => {
    setValue("recurrencePattern.skipDates", _.sortBy(value), {
      shouldDirty: true,
    });
  };

  return {
    skipDates,
    filteredSkippableDatesCollection: collection,
    handleComboboxValueChange,
    handleComboboxInputValueChange,
    handleComboboxOpenChange,
  };
};
